import { useEffect, useState } from 'react';
import { listTags } from '@/services/storyRepo';
import { FILTER_DIMENSIONS } from '@/lib/filters';
import { PRACTICES } from '@/lib/practices';

// Static option providers for dimensions marked source:'static' in
// FILTER_DIMENSIONS. Add new keys here when a new static-sourced dimension
// is introduced (until practices migrate to a shared sadhanas table).
const STATIC_OPTIONS = {
  practice_ids: PRACTICES,
};

// Module-level cache so we hit Supabase only once per page load even when
// multiple components (LandingPage hero + SearchResultsPage) mount and
// remount across navigation. Tag dictionaries are tiny and effectively
// static — refetching them on every render is wasteful.
let cachedTagOptions = null;
let inFlightPromise = null;

async function fetchAll() {
  if (cachedTagOptions) return cachedTagOptions;
  if (inFlightPromise) return inFlightPromise;

  inFlightPromise = (async () => {
    // Static-sourced dimensions short-circuit the fetch; only Supabase-backed
    // dimensions cost a network round-trip.
    const lists = await Promise.all(
      FILTER_DIMENSIONS.map(({ key, dimension, source }) =>
        source === 'static'
          ? Promise.resolve(STATIC_OPTIONS[key] || [])
          : listTags(dimension)
      )
    );
    const map = FILTER_DIMENSIONS.reduce((acc, { key }, i) => {
      acc[key] = lists[i];
      return acc;
    }, {});
    cachedTagOptions = map;
    inFlightPromise = null;
    return map;
  })();

  return inFlightPromise;
}

// Loads (and caches) the tag dictionaries for every dimension declared
// in FILTER_DIMENSIONS. Returns { tagOptions, loading, error } where
// tagOptions is always a safe-to-render shape: one [] per filter key.
const emptyTagMap = () =>
  FILTER_DIMENSIONS.reduce((acc, { key }) => ({ ...acc, [key]: [] }), {});

export function useTagOptions() {
  const [tagOptions, setTagOptions] = useState(cachedTagOptions || emptyTagMap());
  const [loading, setLoading] = useState(!cachedTagOptions);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cachedTagOptions) return;
    let cancelled = false;
    setLoading(true);
    fetchAll()
      .then((map) => {
        if (cancelled) return;
        setTagOptions(map);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to load tag options:', err);
        setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return { tagOptions, loading, error };
}
